import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { CreditCard, AlertTriangle } from "lucide-react";
import { useRevenueAnalytics } from "@/hooks/useRevenueAnalytics";
import { RevenueBreakdownSkeleton } from "@/components/ui/skeleton-block";

export function RevenueBreakdown() {
  const { data, isLoading, error } = useRevenueAnalytics();

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Revenue Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-muted-foreground">
            <p>Unable to load revenue breakdown</p>
            <p className="text-xs mt-1">{error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const calculatePercentage = (amount: number, total: number) => {
    return total > 0 ? (amount / total) * 100 : 0;
  };

  // Use updated RevenueData fields (netRevenue = provider earnings after fees & refunds)
  const totalRevenue = data?.totalRevenue || 0;
  const netRevenue = data?.netRevenue || 0;
  const grossRevenue = data?.grossRevenue || 0;
  const totalRefunds = data?.totalRefunds || 0;
  const feeRevenue = data?.feeRevenue || 0;

  const bookingPercentage = calculatePercentage(netRevenue, totalRevenue);
  const feePercentage = calculatePercentage(feeRevenue, totalRevenue);

  const revenueItems = [
    {
      label: "Service Bookings",
      amount: netRevenue,
      percentage: bookingPercentage,
      icon: CreditCard,
      color: "bg-blue-500",
      description: "Your earnings after Figgy fees, processing fees, and refunds"
    },
    {
      label: "No-Show Fees",
      amount: feeRevenue,
      percentage: feePercentage,
      icon: AlertTriangle,
      color: "bg-orange-500",
      description: "Fees from no-shows and late cancellations"
    }
  ];

  if (isLoading) return <RevenueBreakdownSkeleton />;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Revenue Breakdown</CardTitle>
        <CardDescription>Current month revenue by source</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {totalRevenue === 0 ? (
          <div className="text-center text-muted-foreground py-8">
            <CreditCard className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No revenue data yet</p>
            <p className="text-xs mt-1">Revenue will appear here once you have completed bookings</p>
          </div>
        ) : (
          <>
            <div className="text-center">
              <div className="text-3xl font-bold">{formatCurrency(totalRevenue)}</div>
              <p className="text-sm text-muted-foreground">Total Revenue This Month</p>
            </div>

            <div className="space-y-4">
              {revenueItems.map((item, index) => {
                const Icon = item.icon;
                return (
                  <div key={index} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className={`w-3 h-3 rounded-full ${item.color}`} />
                        <Icon className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-medium">{item.label}</span>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-semibold">{formatCurrency(item.amount)}</div>
                        <div className="text-xs text-muted-foreground">
                          {item.percentage.toFixed(1)}%
                        </div>
                      </div>
                    </div>
                    <Progress value={item.percentage} className="h-2" />
                    <p className="text-xs text-muted-foreground">{item.description}</p>
                  </div>
                );
              })}
            </div>

            {totalRefunds > 0 && (
              <div className="pt-4 border-t">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Total Refunds</span>
                  <span className="font-medium text-red-600">-{formatCurrency(totalRefunds)}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Total refunded to customers (already deducted from realized earnings)
                </p>
              </div>
            )}

            <div className="pt-4 border-t">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-lg font-semibold">{data?.totalBookings || 0}</div>
                  <div className="text-xs text-muted-foreground">Completed Bookings</div>
                </div>
                <div>
                  <div className="text-lg font-semibold">{formatCurrency(grossRevenue)}</div>
                  <div className="text-xs text-muted-foreground">Gross Revenue</div>
                </div>
                <div>
                  <div className="text-lg font-semibold">{data?.activeClients || 0}</div>
                  <div className="text-xs text-muted-foreground">Unique Clients</div>
                </div>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}